import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CSS/checkout.css';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { useAddresses } from '../hooks/useAddresses';
import { useOrders } from '../hooks/useOrders';
import { useShipping } from '../hooks/useShipping';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
// Auth store available if needed
// import { useAuthStore } from '../stores/authStore';
import { LoadingSpinner, EmptyState } from '../componnents/UI';
import { apiService } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import '../componnents/Campaigns/campaigns.css';
import { formatPrice, getProductImageUrl, getProductName } from '../utils/helpers';
import { getProfileCompletion } from '../utils/profileCompletion';
import { Check, ChevronRight, MapPin, Plus, Package, Wallet } from 'lucide-react';

const Checkout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('checkout');
  // User info available from auth store if needed
  const { items, totalPrice, totalItems, isLoading: isLoadingCart, clearCartAsync } = useCart();
  const { addresses, isLoading: isLoadingAddresses } = useAddresses();
  const { createOrderAsync, isCreatingOrder } = useOrders();
  const { shippingFee, calculateShipping, isCalculating } = useShipping();
  const { paymentMethods, isLoading: isLoadingPaymentMethods } = usePaymentMethods();
  const calculateShippingRef = useRef(calculateShipping);
  const lastCalculatedNeighborhoodRef = useRef(null);

  const STEPS = [
    { id: 'cart', label: t('steps.cart'), icon: Package },
    { id: 'address', label: t('steps.address'), icon: MapPin },
    { id: 'payment', label: t('steps.payment'), icon: Wallet },
    { id: 'confirmation', label: t('steps.confirmation'), icon: Check },
  ];

  // Keep ref updated
  useEffect(() => {
    calculateShippingRef.current = calculateShipping;
  }, [calculateShipping]);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponMessage, setCouponMessage] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const { profile } = useAuth();
  const displayUser = profile || user;

  // Set default address on load
  useEffect(() => {
    if (addresses?.length > 0 && !selectedAddress) {
      const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
      setSelectedAddress(defaultAddr);
    }
  }, [addresses, selectedAddress]);

  const cashOnDeliveryMethods = (paymentMethods || []).filter((method) => {
    const code = (method.code || method.type || '').toUpperCase();
    const name = (method.name || method.label || '').toLowerCase();
    return (
      code === 'CASH_ON_DELIVERY' ||
      name.includes('cash on delivery') ||
      name.includes('paiement à la livraison') ||
      name.includes('cod')
    );
  });

  // Cash on delivery is the only supported payment method for now
  useEffect(() => {
    if (!selectedPaymentMethod && cashOnDeliveryMethods.length > 0) {
      setSelectedPaymentMethod(cashOnDeliveryMethods[0]);
    }
  }, [cashOnDeliveryMethods, selectedPaymentMethod]);

  // Calculate shipping when address changes
  // Shipping fee is based on the selected neighborhood (quartier)
  useEffect(() => {
    if (!selectedAddress) {
      // No address selected, use default fee
      calculateShippingRef.current(null);
      lastCalculatedNeighborhoodRef.current = null;
      return;
    }

    const neighborhood =
      selectedAddress.neighborhood || selectedAddress.state;
    if (neighborhood && neighborhood !== lastCalculatedNeighborhoodRef.current) {
      lastCalculatedNeighborhoodRef.current = neighborhood;
      calculateShippingRef.current(neighborhood);
    } else if (!neighborhood) {
      calculateShippingRef.current(null);
    }
  }, [selectedAddress]);

  const subtotal = totalPrice;
  const shipping = shippingFee || 0;
  const tax = 0;
  const total = Math.max(0, subtotal + shipping + tax - discountAmount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !selectedAddress) {
      setCouponMessage('Enter a coupon and select a shipping address first.');
      return;
    }
    if (!isAuthenticated) {
      setCouponMessage('Please sign in to use a coupon.');
      return;
    }
    setIsValidatingCoupon(true);
    setCouponMessage('');
    try {
      const selectedItemIds = items.map((item) => item.id).filter(Boolean);
      const res = await apiService.validateCoupon({
        couponCode: couponCode.trim(),
        shippingAddressId: selectedAddress.id || selectedAddress._id,
        selectedItems: selectedItemIds,
      });
      const data = res?.data;
      if (data?.valid) {
        setDiscountAmount(data.discountAmount || 0);
        setCouponMessage('Coupon applied successfully.');
      } else {
        setDiscountAmount(0);
        setCouponMessage(data?.message || 'Invalid or expired coupon.');
      }
    } catch (err) {
      setDiscountAmount(0);
      setCouponMessage(err.message || 'Could not validate coupon.');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleNextStep = () => {
    setError(null);

    // Validation for each step
    if (currentStep === 0 && (!items || items.length === 0)) {
      setError(t('errors.emptyCart'));
      return;
    }

    if (currentStep === 0) {
      const { isComplete } = getProfileCompletion(displayUser, addresses);
      if (!isComplete) {
        setError(t('errors.incompleteProfile'));
        return;
      }
    }

    if (currentStep === 1 && !selectedAddress) {
      setError(t('errors.noAddress'));
      return;
    }

    if (currentStep === 2 && !selectedPaymentMethod) {
      setError(t('errors.noPaymentMethod'));
      return;
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handlePlaceOrder = async () => {
    setError(null);

    try {
      const { isComplete } = getProfileCompletion(displayUser, addresses);
      if (!isComplete) {
        setError(t('errors.incompleteProfile'));
        return;
      }

      if (!selectedPaymentMethod) {
        setError(t('errors.noPaymentMethod'));
        return;
      }

      // Prepare order data according to API specification (exactly like st_cael)
      // selectedItems should be an array of cart item IDs (from item.id field)
      const selectedItemIds = items.map(item => item.id).filter(Boolean);
      
      if (selectedItemIds.length === 0) {
        setError(t('errors.noItems'));
        return;
      }
      
      // Get payment method code - must match exactly: 'MPESA', 'ORANGE_MONEY', 'AIRTEL_MONEY', or 'CASH_ON_DELIVERY'
      const normalizedPaymentMethod = 'CASH_ON_DELIVERY';
      
      const orderData = {
        paymentMethod: normalizedPaymentMethod,
        shippingAddressId: selectedAddress.id || selectedAddress._id,
        selectedItems: selectedItemIds, // For partial checkout - array of cart item IDs
        notes: orderNotes || "",
        couponCode: couponCode.trim() || undefined,
      };
      
      // Call the actual order API
      const orderResponse = await createOrderAsync(orderData);
      
      // Clear cart after successful order creation
      try {
        await clearCartAsync();
      } catch (clearError) {
        // Don't block the order success flow if cart clearing fails
        // The cart will be invalidated by useOrders hook anyway
      }
      
      setOrderSuccess(orderResponse);
      setCurrentStep(3);
    } catch (err) {
      setError(err.message || t('errors.placeOrderFailed'));
    }
  };

  // Cart Review Step
  const renderCartStep = () => (
    <div className="checkout-step cart-step">
      <h2>{t('cartStep.title')}</h2>
      
      {isLoadingCart ? (
        <LoadingSpinner text={t('cartStep.loading')} />
      ) : items?.length === 0 ? (
        <EmptyState
          icon="cart"
          title={t('cartStep.empty')}
          description={t('cartStep.emptyDescription')}
          actionText={t('cartStep.shopNow')}
          actionLink="/"
        />
      ) : (
        <div className="cart-items-list">
          {items.map((item, idx) => {
            const product = item.product || item;
            return (
              <div key={idx} className="checkout-item">
                <img src={getProductImageUrl(product)} alt="" className="item-image" />
                <div className="item-details">
                  <p className="item-name">{getProductName(product)}</p>
                  {item.sku && (
                    <p className="item-sku">
                      <span className="item-sku-label">SKU:</span>
                      <span className="item-sku-value">{item.sku}</span>
                    </p>
                  )}
                  {(item.size || item.color) && (
                    <p className="item-variant">
                      {item.size && `${t('size', { ns: 'cart' })}: ${item.size}`}
                      {item.size && item.color && ' • '}
                      {item.color && `${t('color', { ns: 'cart' })}: ${item.color}`}
                    </p>
                  )}
                  <p className="item-qty">{t('quantity', { ns: 'cart' })}: {item.quantity}</p>
                </div>
                {/* IMPORTANT: Use item.price first (contains bid price if it's a bid), fallback to product.price */}
                <p className="item-price">{formatPrice((item.price || product.price || 0) * item.quantity)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Helper function to check if address is selected
  const isAddressSelected = (address) => {
    if (!selectedAddress || !address) return false;
    const selectedId = selectedAddress._id || selectedAddress.id;
    const addressId = address._id || address.id;
    return selectedId && addressId && String(selectedId) === String(addressId);
  };

  // Address Step
  const renderAddressStep = () => (
    <div className="checkout-step address-step">
      <h2>{t('addressStep.title')}</h2>
      
      {isLoadingAddresses ? (
        <LoadingSpinner text={t('addressStep.loading')} />
      ) : addresses?.length === 0 ? (
        <div className="no-addresses">
          <p>{t('addressStep.noAddresses')}</p>
          <button className="add-address-btn" onClick={() => navigate('/profile?tab=addresses')}>
            <Plus size={18} />
            {t('addressStep.addNewAddress')}
          </button>
        </div>
      ) : (
        <div className="addresses-grid">
          {addresses.map((address, idx) => {
            const isSelected = isAddressSelected(address);
            return (
            <div
              key={address._id || address.id || `address-${idx}-${address.createdAt || Date.now()}`}
              className={`address-option ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedAddress(address)}
            >
              <div className="address-radio">
                <div className="radio-circle">
                  {isSelected && (
                    <Check size={14} />
                  )}
                </div>
              </div>
              <div className="address-content">
                <div className="address-header">
                  <p className="address-name">{address.fullName}</p>
                  {isSelected && (
                    <span className="selected-badge">{t('addressStep.selected')}</span>
                  )}
                </div>
                <p className="address-phone">{address.phoneNumber}</p>
                <p className="address-line">{address.homeAddress}</p>
                <p className="address-location">
                  {address.city}
                  {(address.neighborhood || address.state) &&
                    `, ${address.neighborhood || address.state}`}{' '}
                  {address.postalCode}
                </p>
                <p className="address-country">{address.country}</p>
                <div className="address-footer">
                  {address.isDefault && <span className="default-tag">{t('addressStep.default')}</span>}
                </div>
              </div>
            </div>
            );
          })}
          <button className="add-new-address" onClick={() => navigate('/profile?tab=addresses')}>
            <Plus size={24} />
            <span>{t('addressStep.addNewAddress')}</span>
          </button>
        </div>
      )}
    </div>
  );

  // Payment Step
  const renderPaymentStep = () => {
    const isPaymentMethodSelected = (method) => {
      if (!selectedPaymentMethod || !method) return false;
      const selectedId = selectedPaymentMethod._id || selectedPaymentMethod.id;
      const methodId = method._id || method.id;
      return selectedId && methodId && String(selectedId) === String(methodId);
    };

    return (
      <div className="checkout-step payment-step">
        <h2>{t('paymentStep.title')}</h2>
        
        {isLoadingPaymentMethods ? (
          <LoadingSpinner text={t('paymentStep.loading')} />
        ) : cashOnDeliveryMethods.length === 0 ? (
          <div className="no-payment-methods">
            <p>{t('paymentStep.noMethods')}</p>
          </div>
        ) : (
          <div className="payment-options">
            {cashOnDeliveryMethods.map((method, idx) => {
              const isSelected = isPaymentMethodSelected(method);
              const methodName = method.name || method.label || t('paymentMethod');
              const methodDesc =
                method.description || t('paymentStep.codDescription');
              
              return (
                <div
                  key={method._id || method.id || `payment-${idx}-${method.type || Date.now()}`}
                  className={`payment-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedPaymentMethod(method)}
                >
                  <div className="payment-radio">
                    <div className="radio-circle">
                      {isSelected && <Check size={14} />}
                    </div>
                  </div>
                  <div className="payment-content">
                    <Package size={24} />
                    <div>
                      <p className="payment-title">{methodName}</p>
                      <p className="payment-desc">{methodDesc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="order-notes">
          <label>{t('paymentStep.orderNotes')}</label>
          <textarea
            value={orderNotes}
            onChange={e => setOrderNotes(e.target.value)}
            placeholder={t('paymentStep.orderNotesPlaceholder')}
          />
        </div>
      </div>
    );
  };

  // Confirmation Step
  const renderConfirmationStep = () => (
    <div className="checkout-step confirmation-step">
      {orderSuccess ? (
        <div className="order-success">
          <div className="success-icon">
            <Check size={48} />
          </div>
          <h2>{t('confirmationStep.orderPlaced')}</h2>
          <p className="order-number">{t('confirmationStep.orderNumber')} #{orderSuccess.orderNumber || orderSuccess._id}</p>
          <p className="success-message">
            {t('confirmationStep.successMessage')}
          </p>
          <div className="success-actions">
            <button className="view-order-btn" onClick={() => navigate('/profile?tab=orders')}>
              {t('confirmationStep.viewOrders')}
            </button>
            <button className="continue-shopping-btn" onClick={() => navigate('/')}>
              {t('confirmationStep.continueShopping')}
            </button>
          </div>
        </div>
      ) : (
        <div className="order-review">
          <h2>{t('confirmationStep.title')}</h2>
          
          <div className="review-section">
            <h3>{t('confirmationStep.items')} ({totalItems})</h3>
            <div className="review-items">
              {items?.slice(0, 3).map((item, idx) => (
                <div key={idx} className="review-item">
                  <img src={getProductImageUrl(item.product || item)} alt="" />
                  <span>{getProductName(item.product || item)}</span>
                  <span>×{item.quantity}</span>
                </div>
              ))}
              {items?.length > 3 && (
                <p className="more-items">+{items.length - 3} {t('confirmationStep.moreItems')}</p>
              )}
            </div>
          </div>

          <div className="review-section">
            <h3>{t('confirmationStep.shippingAddress')}</h3>
            {selectedAddress && (
              <div className="review-address">
                <p>{selectedAddress.fullName}</p>
                <p>{selectedAddress.homeAddress}</p>
                <p>{selectedAddress.city}, {selectedAddress.country}</p>
              </div>
            )}
          </div>

          <div className="review-section">
            <h3>{t('confirmationStep.paymentMethod')}</h3>
            <p className="review-payment">{t('paymentMethod')}</p>
            <p className="review-payment-hint" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {t('confirmationStep.codHint')}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  if (isLoadingCart) {
    return (
      <div className="checkout-page">
        <LoadingSpinner text={t('loading')} fullPage />
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {/* Progress Bar */}
        <div className="checkout-progress">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <div key={step.id} className="progress-item">
                <div
                  className={`progress-step ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                >
                  {index < currentStep ? <Check size={18} /> : <StepIcon size={18} />}
                </div>
                <span className={`progress-label ${index <= currentStep ? 'active' : ''}`}>
                  {step.label}
                </span>
                {index < STEPS.length - 1 && (
                  <div className={`progress-line ${index < currentStep ? 'completed' : ''}`} />
                )}
              </div>
            );
          })}
        </div>

        <div className="checkout-content">
          {/* Main Content */}
          <div className="checkout-main">
            {error && (
              <div className="checkout-error">
                {error}
                {error === t('errors.incompleteProfile') && (
                  <Link to="/profile?complete=1" className="checkout-error-link">
                    {t('profileCompletion.completeNow', { ns: 'profile' })}
                  </Link>
                )}
              </div>
            )}
            
            {currentStep === 0 && renderCartStep()}
            {currentStep === 1 && renderAddressStep()}
            {currentStep === 2 && renderPaymentStep()}
            {currentStep === 3 && renderConfirmationStep()}

            {/* Navigation Buttons */}
            {currentStep < 3 && (
              <div className="checkout-navigation">
                {currentStep > 0 && (
                  <button className="btn-prev" onClick={handlePrevStep}>
                    {t('back')}
                  </button>
                )}
                {currentStep < 2 ? (
                  <button className="btn-next" onClick={handleNextStep}>
                    {t('continue')}
                    <ChevronRight size={18} />
                  </button>
                ) : (
                  <button
                    className="btn-place-order"
                    onClick={handlePlaceOrder}
                    disabled={isCreatingOrder}
                  >
                    {isCreatingOrder ? t('placingOrder') : `${t('placeOrder')} • ${formatPrice(total)}`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          {currentStep < 3 && (
            <aside className="checkout-sidebar">
              <div className="order-summary">
                <h3>{t('orderSummary')}</h3>
                
                <div className="summary-row">
                  <span>{t('subtotal')} ({totalItems} {totalItems > 1 ? t('items', { ns: 'cart' }) : t('item', { ns: 'cart' })})</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                
                <div className="summary-row">
                  <span>{t('shipping')}</span>
                  <span>
                    {isCalculating ? (
                      t('calculating')
                    ) : shipping > 0 ? (
                      formatPrice(shipping)
                    ) : selectedAddress ? (
                      formatPrice(199.99) // Default fee if no province
                    ) : (
                      t('selectAddress')
                    )}
                  </span>
                </div>
                
                {tax > 0 && (
                  <div className="summary-row">
                    <span>{t('tax')}</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                )}

                {currentStep >= 1 && isAuthenticated && (
                  <div className="summary-coupon" style={{ marginTop: '1rem' }}>
                    <label htmlFor="coupon-code" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      Promo code
                    </label>
                    <div className="checkout-coupon-row">
                      <input
                        id="coupon-code"
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="SAVE10"
                      />
                      <button type="button" onClick={handleApplyCoupon} disabled={isValidatingCoupon}>
                        {isValidatingCoupon ? '...' : 'Apply'}
                      </button>
                    </div>
                    {couponMessage && (
                      <p style={{ fontSize: '0.8rem', marginTop: '0.35rem', color: discountAmount > 0 ? '#16a34a' : '#b91c1c' }}>
                        {couponMessage}
                      </p>
                    )}
                  </div>
                )}

                {discountAmount > 0 && (
                  <div className="summary-row checkout-discount-line">
                    <span>Discount</span>
                    <span>-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                
                <div className="summary-row total">
                  <span>{t('total')}</span>
                  <span>{formatPrice(total)}</span>
                </div>

                {selectedAddress && currentStep >= 1 && (
                  <div className="summary-address">
                    <h4>{t('deliveringTo')}</h4>
                    <p>{selectedAddress.fullName}</p>
                    <p>{selectedAddress.city}, {selectedAddress.country}</p>
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
